/**
 * Adapter to convert between legacy Marune notification format and Phoenix format
 */

import { Notification } from '../types/notification';

/**
 * Convert a legacy notification from Firebase to Phoenix format
 */
export const adaptLegacyNotification = (legacyNotif: any): Notification => {
  // Keep legacy types as-is for backward compatibility
  const legacyTypes = ['requestReceived', 'requestAccepted', 'oss', 'friend-belt-prom', 'perk-unlocked'];
  const isLegacyType = legacyTypes.includes(legacyNotif.type);
  
  // Only map if it's not a legacy type
  const type = isLegacyType ? legacyNotif.type : legacyNotif.type;
  
  // Build base notification
  const notification: Notification = {
    id: legacyNotif.key || legacyNotif.id || '',
    key: legacyNotif.key,
    type: type as any,
    recipientId: legacyNotif.recipientId || '',
    read: legacyNotif.isRead || false,
    isRead: legacyNotif.isRead, // Keep legacy field
    dateCreated: legacyNotif.timestamp || legacyNotif.dateCreated || Date.now(),
    timestamp: legacyNotif.timestamp, // Keep legacy field
    eventInitiator: legacyNotif.eventInitiator, // Keep legacy field
    isSysGen: legacyNotif.isSysGen,
    isAdditional: legacyNotif.isAdditional,
  };

  // Handle message/title/body
  if (legacyNotif.message) {
    notification.message = legacyNotif.message;
    notification.title = legacyNotif.message; // Use message as title
  }

  // Preserve title/body from raw data (e.g., ClearMat system notifications from backend)
  if (legacyNotif.title && !notification.title) {
    notification.title = legacyNotif.title;
  }
  if (legacyNotif.body) {
    notification.body = legacyNotif.body;
  }

  // Preserve actionUrl for system notifications
  if (legacyNotif.actionUrl) {
    (notification as any).actionUrl = legacyNotif.actionUrl;
  }

  // Handle eventReference
  if (legacyNotif.eventReference) {
    notification.eventReference = legacyNotif.eventReference;
  }

  // Map sender info based on type
  if (legacyNotif.eventInitiator) {
    (notification as any).senderId = legacyNotif.eventInitiator;
  }

  return notification;
};

/**
 * Convert Phoenix notification to legacy format for Firebase
 */
export const adaptToLegacyNotification = (notification: Notification): any => {
  const legacy: any = {
    type: notification.type,
    timestamp: notification.dateCreated,
    isRead: notification.read,
    recipientId: notification.recipientId,
    key: notification.id
  };

  // Map Phoenix types back to legacy if needed
  const reverseTypeMapping: Record<string, string> = {
    'friendRequest': 'requestReceived',
    'friendAccept': 'requestAccepted',
    'like': 'oss'
  };

  if (reverseTypeMapping[notification.type]) {
    legacy.type = reverseTypeMapping[notification.type];
  }

  // Handle sender info
  if ('senderId' in notification && notification.senderId) {
    legacy.eventInitiator = notification.senderId;
  }

  // Handle message
  if ('title' in notification) {
    legacy.message = notification.title;
  } else if ('body' in notification) {
    legacy.message = notification.body;
  }

  // Preserve flags
  if (notification.isSysGen) {
    legacy.isSysGen = true;
  }
  if (notification.isAdditional) {
    legacy.isAdditional = true;
  }

  return legacy;
};